/**
 * Loads recently viewed products into the section via Search + Section Rendering API.
 */
class RecentlyViewedProducts extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    if (this.dataset.hasProducts === 'true' && this.querySelector('[data-product-id]')) {
      this.dataset.loaded = 'true';
      return;
    }
    this.#loadProducts();
  }

  disconnectedCallback() {
    this.#activeFetch?.abort();
  }

  async #loadProducts() {
    const { RecentlyViewed } = await import('@theme/recently-viewed-products');

    const maxProducts = Number(this.dataset.maxProducts || 6);
    const currentProductId = this.dataset.productId || '';
    const isDesignMode = this.dataset.designMode === 'true';

    const productIds = RecentlyViewed.getProducts()
      .map(String)
      .filter((id) => id && id !== String(currentProductId))
      .slice(0, maxProducts);

    if (productIds.length === 0) {
      if (isDesignMode) {
        this.dataset.hasProducts = 'true';
        this.dataset.loaded = 'true';
        this.hidden = false;
        return;
      }
      this.dataset.hasProducts = 'false';
      this.dataset.loaded = 'true';
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.dataset.hasProducts = 'loading';

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', this.dataset.sectionId || 'recently-viewed');

      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) throw new Error(`Failed to load recently viewed (${response.status})`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newResults = doc.querySelector('[data-recently-viewed-results]');
      const currentResults = this.querySelector('[data-recently-viewed-results]');

      if (!newResults || !currentResults) throw new Error('Recently viewed results not found');

      // Full replace — do not morph skeleton grid into carousel (breaks layout).
      currentResults.replaceWith(document.importNode(newResults, true));

      this.#cleanupEmptySlides();
      this.#reorderItems(productIds);
      this.#refreshSlideshow();

      const hasItems = this.querySelectorAll('[data-product-id]').length > 0;
      this.dataset.hasProducts = hasItems ? 'true' : 'false';
      this.hidden = !hasItems;
      this.dataset.loaded = 'true';
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Recently viewed error:', error);
      this.dataset.hasProducts = 'false';
      this.dataset.loaded = 'true';
      this.hidden = true;
    } finally {
      this.#activeFetch = null;
    }
  }

  /** Drop blank slideshow slides that push cards to the right. */
  #cleanupEmptySlides() {
    const slides = this.querySelector('slideshow-slides');
    if (!slides) return;

    for (const slide of [...slides.querySelectorAll(':scope > slideshow-slide')]) {
      if (!slide.querySelector('[data-product-id]')) {
        slide.remove();
      }
    }
  }

  /**
   * Keep cards in localStorage order (most recent first).
   * @param {string[]} productIds
   */
  #reorderItems(productIds) {
    const slides = this.querySelector('slideshow-slides');
    const list =
      slides || this.querySelector('.resource-list--grid') || this.querySelector('.resource-list');
    if (!list) return;

    /** @type {Map<string, Element>} */
    const byId = new Map();
    for (const item of this.querySelectorAll('[data-product-id]')) {
      if (!(item instanceof HTMLElement)) continue;
      const wrapper = item.closest('slideshow-slide') || item.closest('.resource-list__item') || item;
      byId.set(String(item.dataset.productId), wrapper);
    }

    for (const id of productIds) {
      const node = byId.get(String(id));
      if (node) list.appendChild(node);
    }
  }

  /**
   * Re-mount slideshow after DOM surgery so observers/refs/controls match final slides.
   * Avoids stuck disabled prev/next arrows after async hydrate + reorder.
   */
  #refreshSlideshow() {
    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow?.parentNode) {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      return;
    }

    const parent = slideshow.parentNode;
    const nextSibling = slideshow.nextSibling;
    slideshow.remove();
    parent.insertBefore(slideshow, nextSibling);

    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
